// The Coordinator Workbench's filter as B10 reads and writes it (B10 slice S21; plan D15; CR-084 "4.2 Coordinator
// Workbench — Intakes, recruiter screens, HM interviews, Final Interview Loops and debriefs to schedule."; CR-090
// "Coordinator manages; Recruiter requests/views; HM responds; TA leadership sees SLA analytics.").
//
// B5 owns the Workbench (`views/Workbench.tsx`, `views/scheduling/workbench.ts`) and its URL grammar,
// `#workbench[/<sr_…>]?status=&type=&risk=sla&blocked=1&sync=1&req=`. B10 writes two things into it and reads one:
//   - `workbenchHref` — the drill-in a report row (Coordinator operations, S09/S15) lands on: the same filter the
//     Workbench counts, so "workbench counts reconcile to records" holds for the drill too (D15 "counts equal the
//     Workbench's"). Parameters are written in B5's order, so the href equals the one B5's own `queryString` builds;
//   - `specOfQuery` / `queryOfSpec` — a saved view on surface `workbench` (S10, D6), stored in `scheduling.workbench`'s
//     own input keys (B5 `WorkbenchInput`: `status`, `meeting_type`, `risk`, `blocked`, `sync_failed`,
//     `requisition_id`), never rows; applying one returns the URL filter and the keys the URL cannot carry;
//   - `parseWorkbenchQuery` — B5's `parseQuery`, key for key, so a B10 view reads the URL exactly as the page does.
// Pure: no React, no router (this module loads under the server renderer in the tests).
// The status and meeting-type lists equal B5's (`STATUS_ORDER`, `MEETING_TYPES` in views/scheduling/workbench.ts),
// held here so this module stays free of the design system's labels; tests/mc/b10-workbench.test.mjs compares them,
// and this parse and query string with B5's, on the merged tree.

/** The saved-views surface of the Workbench (S10 `saved_views.register_surface("workbench", …)`). */
export const WORKBENCH_SURFACE = "workbench";

/** The eight scheduling statuses in workflow order (BRD §4.2), B5's stored codes. */
export const WORKBENCH_STATUSES = [
  "requested",
  "in_progress",
  "awaiting_candidate",
  "awaiting_interviewer",
  "scheduled",
  "reschedule_requested",
  "completed",
  "cancelled",
] as const;
export type WorkbenchStatus = (typeof WORKBENCH_STATUSES)[number];

/** The six meeting templates (G §9), B5's stored codes. */
export const WORKBENCH_MEETING_TYPES = ["intake", "recruiter_screen", "hm_interview", "final_loop", "debrief", "offer_call"] as const;
export type WorkbenchMeetingType = (typeof WORKBENCH_MEETING_TYPES)[number];

/** The risks behind the Workbench's one *Scheduling SLA risk* filter (`risk=sla`): at risk and breached, never `none`. */
export const WORKBENCH_SLA_RISKS = ["at_risk", "breached"] as const;

/** A requisition id as the Workbench's `req` carries it. */
const REQ = /^R-\d+$/;

/** The Workbench's filter as its URL carries it — B5's `WorkbenchQuery`, field for field. */
export interface WorkbenchQuery {
  status: WorkbenchStatus | null;
  type: WorkbenchMeetingType | null;
  /** *Scheduling SLA risk*: at risk or breached. */
  risk: boolean;
  /** Blocked for panel/coverage. */
  blocked: boolean;
  /** Sync failure. */
  sync: boolean;
  req: string | null;
}

/** What a drill-in names; anything the Workbench would not read is left out of the href. */
export interface WorkbenchDrill {
  type?: string | null | undefined;
  status?: string | null | undefined;
  /** true, `"sla"`, `"at_risk"` or `"breached"`: the Workbench's *Scheduling SLA risk* filter. */
  risk?: boolean | string | null | undefined;
  blocked?: boolean | null | undefined;
  sync?: boolean | null | undefined;
  req?: string | null | undefined;
}

/** A saved view's spec on surface `workbench`: the non-empty keys of `scheduling.workbench`'s input. */
export type WorkbenchSpec = {
  status?: [WorkbenchStatus];
  meeting_type?: [WorkbenchMeetingType];
  risk?: ["at_risk", "breached"];
  blocked?: true;
  sync_failed?: true;
  requisition_id?: string;
};

export const isWorkbenchStatus = (value: unknown): value is WorkbenchStatus =>
  typeof value === "string" && (WORKBENCH_STATUSES as readonly string[]).includes(value);

export const isWorkbenchMeetingType = (value: unknown): value is WorkbenchMeetingType =>
  typeof value === "string" && (WORKBENCH_MEETING_TYPES as readonly string[]).includes(value);

const isReq = (value: unknown): value is string => typeof value === "string" && REQ.test(value);

/** No filter: every row of the seat's scope. */
export function emptyWorkbenchQuery(): WorkbenchQuery {
  return { status: null, type: null, risk: false, blocked: false, sync: false, req: null };
}

/** True when a drill names the Workbench's SLA risk filter. */
function riskNamed(risk: WorkbenchDrill["risk"]): boolean {
  if (risk === true) return true;
  return risk === "sla" || risk === "at_risk" || risk === "breached";
}

/** The query of `raw` (a route's raw text, a hash or a bare query): B5's `parseQuery`, key for key. */
export function parseWorkbenchQuery(raw: string): WorkbenchQuery {
  const at = raw.indexOf("?");
  const query = new URLSearchParams(at < 0 ? "" : raw.slice(at + 1));
  const status = query.get("status");
  const type = query.get("type");
  const req = query.get("req");
  return {
    status: isWorkbenchStatus(status) ? status : null,
    type: isWorkbenchMeetingType(type) ? type : null,
    risk: query.get("risk") === "sla",
    blocked: query.get("blocked") === "1",
    sync: query.get("sync") === "1",
    req: isReq(req) ? req : null,
  };
}

/** `?status=…&type=…&risk=sla&blocked=1&sync=1&req=…` in B5's order ("" when nothing is set): B5's `queryString`. */
export function workbenchQueryString(q: Partial<WorkbenchQuery>): string {
  const query = new URLSearchParams();
  if (isWorkbenchStatus(q.status)) query.set("status", q.status);
  if (isWorkbenchMeetingType(q.type)) query.set("type", q.type);
  if (q.risk) query.set("risk", "sla");
  if (q.blocked) query.set("blocked", "1");
  if (q.sync) query.set("sync", "1");
  if (isReq(q.req)) query.set("req", q.req);
  const text = query.toString();
  return text ? `?${text}` : "";
}

/**
 * The Workbench under a drill's filter: `#workbench?type=&status=&risk=` written the way the Workbench reads it. A
 * value the Workbench does not know is left out rather than written (it would be ignored there, and the drill would
 * silently widen); `risk` of any SLA kind becomes `risk=sla`, the Workbench's one *Scheduling SLA risk* filter.
 */
export function workbenchHref(drill: WorkbenchDrill = {}): string {
  return `#workbench${workbenchQueryString({
    status: isWorkbenchStatus(drill.status) ? drill.status : null,
    type: isWorkbenchMeetingType(drill.type) ? drill.type : null,
    risk: riskNamed(drill.risk),
    blocked: drill.blocked === true,
    sync: drill.sync === true,
    req: isReq(drill.req) ? drill.req : null,
  })}`;
}

/** The spec a saved view keeps for this filter: only what is set, in `scheduling.workbench`'s input keys. */
export function specOfQuery(q: Partial<WorkbenchQuery>): WorkbenchSpec {
  const spec: WorkbenchSpec = {};
  if (isWorkbenchStatus(q.status)) spec.status = [q.status];
  if (isWorkbenchMeetingType(q.type)) spec.meeting_type = [q.type];
  if (q.risk) spec.risk = ["at_risk", "breached"];
  if (q.blocked) spec.blocked = true;
  if (q.sync) spec.sync_failed = true;
  if (isReq(q.req)) spec.requisition_id = q.req;
  return spec;
}

/** One value of a closed set from a scalar or a one-element list; null for no filter; undefined when it cannot be one. */
function single<T extends string>(value: unknown, guard: (v: unknown) => v is T): T | null | undefined {
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return value.length === 1 && guard(value[0]) ? value[0] : undefined;
  }
  return guard(value) ? value : undefined;
}

/** The SLA risk filter of a spec value: false for none, true for exactly at risk and breached, undefined otherwise. */
function riskOfSpec(value: unknown): boolean | undefined {
  if (value === true || value === "sla") return true;
  if (value === false) return false;
  if (!Array.isArray(value)) return undefined;
  if (value.length === 0) return false;
  const named = new Set(value);
  return named.size === WORKBENCH_SLA_RISKS.length && WORKBENCH_SLA_RISKS.every((risk) => named.has(risk)) ? true : undefined;
}

/**
 * The Workbench filter a saved view's spec applies, and the spec keys it could not apply (in the spec's order). Reads
 * `scheduling.workbench`'s input keys, and B5's `WorkbenchFilter` plural keys and the URL's own keys too, so a view
 * saved by either shape applies. A value the Workbench's URL cannot carry (two statuses, *not* blocked, a partial risk
 * set, another `view`, an unknown key) is dropped and named, never widened silently; paging keys are not filters.
 */
export function queryOfSpec(spec: Readonly<Record<string, unknown>>): { query: WorkbenchQuery; dropped: string[] } {
  const query = emptyWorkbenchQuery();
  const dropped: string[] = [];
  const drop = (key: string) => {
    if (!dropped.includes(key)) dropped.push(key);
  };
  for (const [key, value] of Object.entries(spec)) {
    if (value === null || value === undefined) continue;
    switch (key) {
      case "status":
      case "statuses": {
        const status = single(value, isWorkbenchStatus);
        if (status === undefined) drop(key);
        else if (status !== null) query.status = status;
        break;
      }
      case "meeting_type":
      case "meeting_types":
      case "type": {
        const type = single(value, isWorkbenchMeetingType);
        if (type === undefined) drop(key);
        else if (type !== null) query.type = type;
        break;
      }
      case "risk":
      case "risks": {
        const risk = riskOfSpec(value);
        if (risk === undefined) drop(key);
        else query.risk = query.risk || risk;
        break;
      }
      case "blocked":
        if (value === true) query.blocked = true;
        else drop(key);
        break;
      case "sync_failed":
      case "sync":
        if (value === true) query.sync = true;
        else drop(key);
        break;
      case "requisition_id":
      case "req":
        if (isReq(value)) query.req = value;
        else drop(key);
        break;
      case "view":
        if (value !== "all") drop(key); // the Workbench lists every group, open and history, under one filter
        break;
      case "offset":
      case "limit":
        break; // paging, not a filter
      default:
        drop(key);
    }
  }
  return { query, dropped };
}
